import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './PaymentPorterPage.css';
import paymentporterService from '../services/paymentporterService';

// The two kinds of rule. Chosen FIRST and fixed for the rule's life — the server refuses a change of type — because a
// rule's routing and profile mean different things in each.
const RULE_TYPES = [
    {
        key: 'VCC',
        title: 'Virtual Credit Card (VCC)',
        hint: 'Move the reservation\'s virtual card to a folio window and route charges to the payee profile.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.6"/>
                <path d="M2 10h20M6 15h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
        ),
    },
    {
        key: 'DB',
        title: 'Direct Bill (DB)',
        hint: 'Bill the reservation to a company\'s AR account through the City Ledger routing.',
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M6 3h9l4 4v14H6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                <path d="M9 11h7M9 15h7M9 7h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
        ),
    },
];

const PaymentPorterNewRulePage = () => {
    const navigate        = useNavigate();
    const { ppId }        = useParams();

    const [pp, setPp]             = useState(null);
    const [step, setStep]         = useState(1);
    const [ruleType, setRuleType] = useState(null);
    const [name, setName]         = useState('');
    const [description, setDesc]  = useState('');
    const [saving, setSaving]     = useState(false);
    const [error, setError]       = useState(null);

    useEffect(() => {
        paymentporterService.getById(ppId)
            .then(res => setPp(res.data?.paymentPorter || null))
            .catch(() => {});
    }, [ppId]);

    const handleCreate = async () => {
        if (!name.trim() || !ruleType) return;
        setSaving(true);
        setError(null);
        try {
            const res = await paymentporterService.createRule(ppId, { name: name.trim(), description: description.trim(), rule_type: ruleType });
            const ruleId = res.data?.id;
            navigate(`/apps/payment-porter/${ppId}/rules/${ruleId}`);
        } catch (err) {
            setError(err.response?.data?.error?.message || 'Failed to create rule');
            setSaving(false);
        }
    };

    const hasCityLedger = !!pp?.city_ledger_code;
    const chosen = RULE_TYPES.find(t => t.key === ruleType);

    return (
        <div className="pp-page">
            <div className="pp-header">
                <button className="pp-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {pp?.name || 'Payment Porter'}
                </button>
                <div className="pp-title-row">
                    <div className="pp-title-icon" style={{ background: '#f0fdf4', color: '#16a34a' }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.75"/>
                            <path d="M8 12l3 3 5-5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <div>
                        <h1 className="pp-title">New Rule</h1>
                        {pp?.name && <p className="pp-subtitle-text">{pp.hotel_name || pp.account_name || pp.name}</p>}
                    </div>
                </div>
            </div>

            <div className="pp-steps">
                {step === 1 && (
                <div className="pp-step">
                    <div className="pp-step-heading">
                        <h2 className="pp-step-title">Choose the type of rule</h2>
                        <p className="pp-step-subtitle">A rule is either Virtual Credit Card or Direct Bill. This cannot be changed after the rule is created.</p>
                    </div>
                    <div className="pp-criteria-type-grid">
                        {RULE_TYPES.map(t => {
                            const unavailable = t.key === 'DB' && !hasCityLedger;
                            return (
                                <button
                                    key={t.key}
                                    className={`pp-criteria-type-card${ruleType === t.key ? ' pp-criteria-type-card-selected' : ''}${unavailable ? ' pp-criteria-type-card-disabled' : ''}`}
                                    onClick={() => !unavailable && setRuleType(t.key)}
                                    disabled={unavailable}
                                    title={unavailable ? 'Set the City Ledger on the Settings tab first' : t.hint}
                                >
                                    <span className="pp-criteria-type-card-icon">{t.icon}</span>
                                    <span className="pp-criteria-type-card-title">{t.title}</span>
                                    {ruleType === t.key && (
                                        <span className="pp-criteria-type-card-check">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {chosen && <p className="pp-step-subtitle" style={{ marginTop: '0.75rem' }}>{chosen.hint}</p>}
                    {pp && !hasCityLedger && (
                        <p className="pp-step-subtitle" style={{ marginTop: '0.75rem', color: '#b45309' }}>
                            Direct Bill needs a City Ledger routing code.{' '}
                            <button type="button" className="pp-link-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`, { state: { tab: 'settings' } })}>
                                Set it on the Settings tab
                            </button>.
                        </p>
                    )}
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1rem', maxWidth: 480 }}>
                        <button className="pp-creds-cancel" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>Cancel</button>
                        <button className="pp-creds-save" onClick={() => setStep(2)} disabled={!ruleType}>Next</button>
                    </div>
                </div>
                )}

                {step === 2 && (
                <div className="pp-step">
                    <div className="pp-step-heading">
                        <h2 className="pp-step-title">Name your {chosen?.title || ''} rule</h2>
                        <p className="pp-step-subtitle">Give this rule a descriptive name. You can configure criteria and actions after saving.</p>
                    </div>
                    <div className="pp-criteria-card" style={{ maxWidth: 480 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '0.25rem 0' }}>
                            <div>
                                <label className="pp-name-modal-label">
                                    Name <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <input
                                    className="pp-name-modal-input"
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && name.trim()) handleCreate(); }}
                                    placeholder="e.g. Corporate bookings"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="pp-name-modal-label">Description</label>
                                <input
                                    className="pp-name-modal-input"
                                    type="text"
                                    value={description}
                                    onChange={e => setDesc(e.target.value)}
                                    placeholder="Optional — describe what this rule does"
                                />
                            </div>
                            {error && <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: 0 }}>{error}</p>}
                            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '0.5rem' }}>
                                <button
                                    className="pp-creds-cancel"
                                    onClick={() => setStep(1)}
                                    disabled={saving}
                                >
                                    Back
                                </button>
                                <button
                                    className="pp-creds-save"
                                    onClick={handleCreate}
                                    disabled={saving || !name.trim()}
                                >
                                    {saving ? 'Creating…' : 'Create Rule'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </div>
        </div>
    );
};

export default PaymentPorterNewRulePage;
